<template>
    <vx-modal v-model:visible="visible" size="lg" :padding="false" data-test="audiences-modal">
        <page-select
            v-model="selectedAudiences"
            :options="mergedOptions"
            :loading="loading"
            :getOptionKey="getOptionKey"
            @search="fetchAudiences"
            data-test="page-select-audiences"
        >
            <template v-slot:header>Choose Audiences</template>
            <template v-slot:subheader>You can choose to send to 1 or more audiences.</template>

            <template v-slot:select-all-right>
                <div class="grid grid-cols-[136px_100px] gap-4 items-center">
                    <div class="text-sm hidden sm:block">Source</div>
                    <div class="text-sm text-right pr-7 hidden sm:block">Size</div>
                </div>
            </template>

            <template v-slot:before-items>
                <vx-alert
                    v-if="hasMixedTypes"
                    color="info"
                    :closeable="false"
                    class="mb-2 px-2! lg:px-3! [&>div]:gap-0.5!"
                    data-test="mixing-warning"
                >
                    <template v-slot:icon>
                        <font-awesome-icon :icon="faDiamondExclamation" />
                    </template>
                    <div class="flex justify-between items-center">
                        <span class="text-sm lg:text-base"
                            >Voxie audience sources cannot be mixed with external sources.</span
                        >
                        <vx-button
                            @click="resetSelection"
                            size="sm"
                            color="info"
                            class="flex-shrink-0 ml-2"
                            data-test="reset-selection"
                        >
                            Reset
                            <font-awesome-icon :icon="faRotateLeft" />
                        </vx-button>
                    </div>
                </vx-alert>
            </template>

            <template v-slot:option="{ item }">
                <div class="grid grid-cols-1 sm:grid-cols-[1fr_120px_100px] gap-4 items-center">
                    <div :title="item.name">
                        <div v-if="!onCorporate() && item.is_template" class="flex gap-1">
                            <font-awesome-icon
                                widthAuto
                                :icon="faCrown"
                                class="text-sm text-amber-400"
                            ></font-awesome-icon>
                            <span class="text-xs text-slate-700"> Corporate Segment </span>
                        </div>
                        <div class="font-bold line-clamp-1">{{ item.name }}</div>
                    </div>

                    <div class="text-sm hidden sm:block">
                        {{ getSource(item) }}
                    </div>

                    <div class="text-sm text-right pr-4 hidden sm:block">
                        <template v-if="getContactCount(item) !== null && getContactCount(item) !== undefined">
                            {{ formatCount(getContactCount(item)) }}
                        </template>
                        <vx-icon-loading v-else class="w-4 h-4" spin />
                    </div>
                </div>
            </template>
        </page-select>

        <template v-slot:footer>
            <div class="flex justify-end gap-4">
                <vx-button @click="visible = false" color="muted" size="lg" class="px-5"> Cancel </vx-button>
                <vx-button
                    @click="handleConfirm"
                    color="primary"
                    size="lg"
                    class="flex-grow"
                    :disabled="selectedAudiences.length === 0 || hasMixedTypes"
                    data-test="confirm-audiences"
                >
                    Confirm
                    <font-awesome-icon widthAuto :icon="faCircleCheck" />
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script setup lang="ts">
import { faCircleCheck, faCrown, faDiamondExclamation, faRotateLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxButton, VxIconLoading, VxModal } from '@voxie/frontend-components';
import { computed, inject, ref, watch } from 'vue';
import PageSelect from '~/components/general/PageSelect.vue';
import { externalListVendor, formatCount } from '~/components/filters';
import { useSegmentsContactsCount } from '~/composables/useSegmentsContactsCount';
import { useExternalListsContactCount } from '~/composables/useExternalListsContactCount';
import { catchRequestError } from '~/utils/request';
import { useToasted } from '~/composables/useToasted';
import type { Features } from '~/provides/features.types';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { externalListsClient, segmentsClient } from '~/services';
import type { GetSegmentsParams } from '~/services/segments/segmentsClient.types';
import debounce from '~/utils/debounce';
import type { SegmentAudienceType } from '~/constants/segment';

export interface AudienceItem {
    id: number;
    name: string;
    label: string;
    sourceType: 'segments' | 'external-contact-lists';
    vendor?: string;
    disabled?: boolean;
    [key: string]: unknown;
}

const props = defineProps<{
    audienceType: SegmentAudienceType;
    groupIds?: string[];
    flowId?: string;
    modelValue: AudienceItem[];
}>();

const emit = defineEmits<{
    confirm: [value: AudienceItem[]];
}>();

const visible = defineModel<boolean>('visible', {
    type: Boolean,
    default: false,
});

const selectedAudiences = ref<AudienceItem[]>([...props.modelValue]);

const { contactsCount: segmentContactsCount, fetchSegmentsContactsCount } = useSegmentsContactsCount();
const { contactsCount: externalListContactsCount, fetchExternalListsContactCount } = useExternalListsContactCount();

const loading = ref(false);
const segments = ref<AudienceItem[]>([]);
const externalLists = ref<AudienceItem[]>([]);
const error = ref('');

const toasted = useToasted();
const FEATURES = inject('FEATURES') as Features;

const { onCorporate, currentGroup, currentOrgUnit } = useCurrentGroup();

const teamId = Spark.state.currentTeam.id;

const selectedSourceType = computed(() => {
    return selectedAudiences.value[0]?.sourceType;
});

const mergedOptions = computed(() => {
    const combined = [...segments.value, ...externalLists.value];
    return combined
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
        .map((option) => {
            option.disabled = selectedSourceType.value && selectedSourceType.value !== option.sourceType;
            return option;
        });
});

const debouncedFetchAudiences = debounce(async (query?: string) => {
    error.value = '';

    try {
        const context = {
            teamId,
            orgUnitId: currentOrgUnit.value?.id,
            groupId: currentGroup.value?.group_id,
        };

        const params: GetSegmentsParams = {
            per_page: 10,
            query,
            filter: {
                audience_type: props.audienceType,
                flow: FEATURES.flows
                    ? {
                          in_flow: props.flowId ? 1 : 0,
                          flow_id: props.flowId,
                      }
                    : undefined,
                is_template: onCorporate() ? 0 : undefined,
            },
        };

        const segmentsResponse = await segmentsClient.getSegments(context, params);

        segments.value = segmentsResponse.data.data.map((seg) => ({
            ...seg,
            sourceType: 'segments' as const,
            label: `${seg.id} - ${seg.name}`,
        })) as AudienceItem[];

        try {
            await fetchSegmentsContactsCount(
                context,
                segments.value.map((s) => s.id),
                { group_ids: props.groupIds }
            );
        } catch (e) {
            console.warn('Failed to fetch segment contact counts:', e);
        }

        if (FEATURES.external_contact_lists) {
            const externalListsResponse = await externalListsClient.getExternalLists(context, {
                per_page: 10,
                query,
            });

            externalLists.value = externalListsResponse.data.data.map(
                (list: { id: number; name: string; vendor: string }) => ({
                    ...list,
                    sourceType: 'external-contact-lists' as const,
                    label: `${list.id} - ${list.name}`,
                })
            ) as AudienceItem[];

            try {
                await fetchExternalListsContactCount(context, {
                    external_contact_list_ids: externalLists.value.map((l) => l.id),
                    group_ids: props.groupIds,
                    audience_type: props.audienceType,
                });
            } catch (e) {
                console.warn('Failed to fetch external list contact counts:', e);
            }
        }
    } catch (e) {
        toasted.error(catchRequestError(e));
    } finally {
        loading.value = false;
    }
}, 300);

const fetchAudiences = (query?: string) => {
    loading.value = true;
    debouncedFetchAudiences(query);
};

const hasMixedTypes = computed(() => {
    if (selectedAudiences.value.length === 0) {
        return false;
    }

    const types = new Set(selectedAudiences.value.map((item) => item.sourceType));
    return types.size > 1;
});

const getOptionKey = (option: AudienceItem) => {
    return `${option.sourceType}_${option.id}`;
};

const getSource = (item: AudienceItem) => {
    if (item.sourceType === 'segments') {
        return 'Voxie';
    }
    return externalListVendor(item.vendor ?? null);
};

const getContactCount = (item: AudienceItem) => {
    if (item.sourceType === 'segments') {
        return segmentContactsCount.value[item.id];
    }

    return externalListContactsCount.value[item.id];
};

const resetSelection = () => {
    selectedAudiences.value = [];
};

const handleConfirm = () => {
    emit('confirm', [...selectedAudiences.value]);
};

watch(
    () => props.modelValue,
    (newValue) => {
        if (!visible.value) {
            selectedAudiences.value = [...newValue];
        }
    },
    { deep: true }
);

watch(visible, (isVisible) => {
    if (isVisible) {
        selectedAudiences.value = [...props.modelValue];
    }
});

watch(
    [visible, () => props.groupIds, () => props.audienceType, () => props.flowId],
    () => {
        if (visible.value) {
            fetchAudiences();
        }
    },
    {
        immediate: true,
    }
);
</script>
